import { Injectable } from '@angular/core';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { BackgroundGeolocationPlugin, Location, WatcherOptions } from '@capacitor-community/background-geolocation';
import { LoggerWrapper } from '../logger/logger-wrapper.service';
import { LoggerFactory } from '../logger/logger-factory.service';
import { Geolocation, GeolocationPermissionType, Position } from '@capacitor/geolocation';
import { PermissionStatus } from '@capacitor/geolocation/dist/esm/definitions';
import { AlertController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { StorageProvider } from '../storage/storage';
import { RequestsBackgroundProvider } from '../requests-background/requests-background';
import { environment } from '../../../../environments/environment';
import { UserService } from '../auth/user.service';
import { getConfigOrElse } from '../config/config.service';
import { BatteryOptimizationService } from '../battery-optimization/battery-optimization.service';
import { BehaviorSubject } from 'rxjs';

const BackgroundGeolocation: BackgroundGeolocationPlugin = registerPlugin('BackgroundGeolocation');

export interface SendPositionGpsDto {
  id_intervenant: string;
  send_to_optimap: boolean;
  position_gps: PositionGpsDto;
}

export interface PositionGpsDto {
  gps_latitude?: string;
  gps_longitude?: string;
}

export type LocationForActivite = {
  idActivite: string;
  location: Location;
};

export enum LOCALISATION_PRECISION_TYPE {
  NORMAL,
  PRECISE,
}

export type LocalisationPermissionStatus = 'disabled' | 'no_localisation' | 'no_fine_localisation' | 'granted';

/**
 * GeoLocalisationProvider
 *
 * Ce service permet de récupérer la localisation du téléphone
 */
@Injectable({ providedIn: 'root' })
export class GeoLocalisationProvider {
  private readonly logger: LoggerWrapper;
  private readonly WATCHER_STORAGE_KEY = '_background_geolocation_watcher_id';

  lastPositionGps$: BehaviorSubject<LocationForActivite> = new BehaviorSubject(null);

  /**
   * Constructeur
   *
   * Crée une instance de GeoLocalisationProvider.
   *
   * @param alertController
   * @param translate
   * @param storageProvider
   * @param loggerFactory
   * @param requestsBackgroundProvider
   * @param userService
   * @param batteryOptimizationService
   */
  constructor(
    private alertController: AlertController,
    private translate: TranslateService,
    private storageProvider: StorageProvider,
    private loggerFactory: LoggerFactory,
    private requestsBackgroundProvider: RequestsBackgroundProvider,
    private userService: UserService,
    private batteryOptimizationService: BatteryOptimizationService
  ) {
    this.logger = this.loggerFactory.getInstance(this, true);
  }

  /**
   * Arrêt de la geolocalisation
   */
  async stopGeolocation(idOt: string) {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    let watcherId = (await this.storageProvider.get(this.computeWatcherIdStorageKey(idOt), false)) as string;
    if (watcherId) {
      this.logger.info(`Removing watcher id ${watcherId} for ot id ${idOt}`);
      await BackgroundGeolocation.removeWatcher({ id: watcherId });
      await this.storageProvider.remove(this.computeWatcherIdStorageKey(idOt));
      this.lastPositionGps$.next(null);
    }
  }

  async pushGeoPosition(sendToOptimap: boolean, gps_latitude: string, gps_longitude: string) {
    this.logger.info('pushGeoPosition function started');
    let data: PositionGpsDto = {
      gps_latitude: gps_latitude,
      gps_longitude: gps_longitude,
    };
    try {
      await this.sendPositionGps(sendToOptimap, data);
      this.logger.info('pushGeoPosition ended');
    } catch (error) {
      this.logger.error(error, 'error posting GPS position');
    }
  }

  async verifierPermissionsGeoloc(): Promise<LocalisationPermissionStatus> {
    if (!Capacitor.isNativePlatform()) {
      return 'granted';
    }
    if (!(await this.checkLocalisationEnabled())) {
      await this.openGpsDisabledAlert();
      return 'disabled';
    }
    if (!(await this.checkLocationPermission(LOCALISATION_PRECISION_TYPE.NORMAL))) {
      await this.openSettingAlertForLocalisationPermission(LOCALISATION_PRECISION_TYPE.NORMAL);
      return 'no_localisation';
    }
    if (!(await this.checkLocationPermission(LOCALISATION_PRECISION_TYPE.PRECISE))) {
      await this.openSettingAlertForLocalisationPermission(LOCALISATION_PRECISION_TYPE.PRECISE);
      return 'no_fine_localisation';
    }
    return 'granted';
  }

  private async checkLocalisationEnabled() {
    try {
      await Geolocation.checkPermissions();
      return true;
    } catch (error) {
      this.logger.error(error, 'Localisation désactivée sur le téléphone');
      return false;
    }
  }

  async hasGeolocAndBatteryPermissions(): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) {
      return true;
    }
    if ((await this.hasGeolocationPermission(LOCALISATION_PRECISION_TYPE.NORMAL)) == false) {
      return false;
    }

    if (await this.batteryOptimizationService.isBatteryOptimizationEnabled()) {
      await this.batteryOptimizationService.requestIgnoreBatteryOptimization();
      return false;
    }

    return true;
  }

  async hasGeolocationPermission(localisationPrecisionType: LOCALISATION_PRECISION_TYPE): Promise<boolean> {
    if (!(await this.isLocationPermissionGranted(localisationPrecisionType))) {
      const granted = await this.requestLocationPermission(localisationPrecisionType);
      if (!granted) {
        await this.openSettingAlertForLocalisationPermission(localisationPrecisionType);
        return false;
      }
      return false;
    }
    return true;
  }

  async checkLocationPermission(localisationPreciseType: LOCALISATION_PRECISION_TYPE): Promise<boolean> {
    if (await this.isLocationPermissionGranted(localisationPreciseType)) {
      return true;
    }
    return await this.requestLocationPermission(localisationPreciseType);
  }
  private async isLocationPermissionGranted(localisationPrecisionType: LOCALISATION_PRECISION_TYPE): Promise<boolean> {
    try {
      const status: PermissionStatus = await Geolocation.checkPermissions();
      let permissionGranted: boolean;

      if (localisationPrecisionType === LOCALISATION_PRECISION_TYPE.NORMAL) {
        permissionGranted = status.coarseLocation === 'granted';
        this.logger.debug('is Location Normal Permission Granted: ', status);
      } else {
        permissionGranted = status.location === 'granted';
        this.logger.debug('is Location Precise Permission Granted: ', status);
      }
      return permissionGranted;
    } catch (error) {
      this.logger.error(error, 'Localisation désactivée sur le téléphone');
      return false;
    }
  }

  private async requestLocationPermission(localisationPrecisionType: LOCALISATION_PRECISION_TYPE): Promise<boolean> {
    try {
      let permissionType: GeolocationPermissionType =
        localisationPrecisionType === LOCALISATION_PRECISION_TYPE.NORMAL ? 'coarseLocation' : 'location';

      this.logger.debug('requestLocationPermission Start');
      const permission: PermissionStatus = await Geolocation.requestPermissions({
        permissions: [permissionType],
      });
      let permissionGranted = permission.coarseLocation === 'granted';
      this.logger.debug('requestLocationPermission: ', permission);
      return permissionGranted;
    } catch (error) {
      this.logger.error(error, 'Erreur lors de la demande de permission');
      return false;
    }
  }

  async startGeolocation(
    idOT: string,
    sendToOptimap: boolean,
    backgroundTitle: string,
    backgroundMessage: string
  ): Promise<any> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    try {
      const geolocDistanceFilter = Number(getConfigOrElse('BACKGROUND_GEOLOCATION_DISTANCE_FILTER_METRES', '0'));
      const geolocInterval = Number(getConfigOrElse('BACKGROUND_GEOLOCATION_INTERVAL_MINUTES', '1')) * 60 * 1000;

      let lastCallbackTime: number | null = null;
      let watcherId: string;
      let self = this;
      const backgroundGeolocationWatcherOptions: WatcherOptions = {
        stale: true,
        backgroundTitle: backgroundTitle,
        backgroundMessage: backgroundMessage,
        requestPermissions: true,
        distanceFilter: geolocDistanceFilter,
      };

      watcherId = await BackgroundGeolocation.addWatcher(
        backgroundGeolocationWatcherOptions,
        function callback(location, error: any): void {
          if (error) {
            if (error.code === 'NOT_AUTHORIZED') {
              self.openSettingAlertForLocalisationPermission(LOCALISATION_PRECISION_TYPE.NORMAL);
            }
            return this.logError(error);
          }

          const now = Date.now();

          if (lastCallbackTime === null || now - lastCallbackTime >= geolocInterval) {
            lastCallbackTime = now;

            self.logger.info('GeoPosition is going to be sent for activite ' + idOT);
            self.lastPositionGps$.next({
              location: location,
              idActivite: idOT,
            });
            self.pushGeoPosition(sendToOptimap, location.latitude.toString(), location.longitude.toString());
            self.logger.info(
              'getting',
              location.latitude,
              location.longitude,
              'watcherId',
              watcherId,
              'activite',
              idOT
            );
          }
        }
      );

      this.logger.info(`Creating watcher id ${watcherId} for ot id ${idOT}`);
      await this.storageProvider.set(this.computeWatcherIdStorageKey(idOT), watcherId, false);
    } catch (error) {
      this.logger.error(error, 'error adding background geolocation watcher for idOT' + idOT);
    }
  }

  async openGpsDisabledAlert() {
    const alert = await this.alertController.create({
      header: this.translate.instant('GEOLOCATION.GPS_DISABLED_ALERT.TITLE'),
      message: this.translate.instant('GEOLOCATION.GPS_DISABLED_ALERT.MESSAGE'),
      buttons: [
        {
          text: this.translate.instant('GLOBAL.OK'),
        },
      ],
    });
    await alert.present();
  }

  async openSettingAlertForLocalisationPermission(localisationPrecisionType: LOCALISATION_PRECISION_TYPE) {
    let headerMessage: string;
    let contentMessage: string;
    let buttonMessage: string;
    if (localisationPrecisionType == LOCALISATION_PRECISION_TYPE.NORMAL) {
      headerMessage = this.translate.instant('GEOLOCATION.PERMISSION_REQUIRED_ALERT.TITLE');
      contentMessage = this.translate.instant('GEOLOCATION.PERMISSION_REQUIRED_ALERT.MESSAGE');
      buttonMessage = this.translate.instant('GEOLOCATION.PERMISSION_REQUIRED_ALERT.OPEN_SETTINGS');
    } else {
      headerMessage = this.translate.instant('GEOLOCATION.PERMISSION_PRECISE_REQUIRED_ALERT.TITLE');
      contentMessage = this.translate.instant('GEOLOCATION.PERMISSION_PRECISE_REQUIRED_ALERT.MESSAGE');
      buttonMessage = this.translate.instant('GEOLOCATION.PERMISSION_PRECISE_REQUIRED_ALERT.OPEN_SETTINGS');
    }

    const alert = await this.alertController.create({
      header: headerMessage,
      message: contentMessage,
      buttons: [
        {
          text: buttonMessage,
          handler: () => {
            BackgroundGeolocation.openSettings();
          },
        },
        {
          text: this.translate.instant('GLOBAL.CANCEL'),
          role: 'cancel',
        },
      ],
    });
    await alert.present();
  }
  computeWatcherIdStorageKey(id: string): string {
    return id + this.WATCHER_STORAGE_KEY;
  }

  async sendPositionGps(sendToOptimap: boolean, data: PositionGpsDto) {
    let url = environment.apis.API_DACTECH_BACK_ROOT + '/v1/gps/position';

    const dto: SendPositionGpsDto = {
      id_intervenant: this.userService.getId(),
      position_gps: data,
      send_to_optimap: sendToOptimap,
    };

    await this.requestsBackgroundProvider.postCapacitorHttpRequest(url, dto);
  }

  async getCurrentPosition(): Promise<Position> {
    const MAX_ATTEMPTS = 3;
    let attempts = 0;
    let currentPosition = null;

    while (currentPosition == null && attempts < MAX_ATTEMPTS) {
      try {
        currentPosition = await Geolocation.getCurrentPosition({
          enableHighAccuracy: true,
          timeout: 30000,
          maximumAge: 0,
        });
      } catch (error) {
        this.logger.error(error, 'error getting current position');
      } finally {
        attempts++;
      }
    }
    this.logger.info('Final Current position = ', currentPosition);
    return currentPosition;
  }
}
